import { genres } from "@/mocks/genres";
import { allYears } from "@/lib/movie";
import type { SortKey } from "@/lib/movie";

interface FilterBarProps {
  selectedGenres: string[];
  onToggleGenre: (slug: string) => void;
  year: number | "all";
  onYearChange: (v: number | "all") => void;
  minRating: number;
  onRatingChange: (v: number) => void;
  sort: SortKey;
  onSortChange: (v: SortKey) => void;
  onReset: () => void;
}

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "release", label: "按上映时间" },
  { key: "rating", label: "按评分" },
  { key: "popularity", label: "按热度" },
];

export default function FilterBar({
  selectedGenres,
  onToggleGenre,
  year,
  onYearChange,
  minRating,
  onRatingChange,
  sort,
  onSortChange,
  onReset,
}: FilterBarProps) {
  return (
    <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
      <div className="flex flex-col gap-5">
        <div>
          <p className="text-xs font-semibold text-foreground-600 mb-2.5">类型</p>
          <div className="flex flex-wrap gap-2">
            {genres.map((g) => {
              const active = selectedGenres.includes(g.slug);
              return (
                <button
                  key={g.slug}
                  type="button"
                  onClick={() => onToggleGenre(g.slug)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                  }`}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-semibold text-foreground-600 mb-2.5">上映年份</p>
            <div className="relative">
              <select
                value={String(year)}
                onChange={(e) =>
                  onYearChange(e.target.value === "all" ? "all" : Number(e.target.value))
                }
                className="w-full appearance-none rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 px-3 py-2.5 pr-9 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="all">全部年份</option>
                {allYears.map((y) => (
                  <option key={y} value={y}>
                    {y} 年
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-xs font-semibold text-foreground-600">最低评分</p>
              <span className="text-xs font-medium text-accent-600">{minRating.toFixed(1)} 分以上</span>
            </div>
            <input
              type="range"
              min={0}
              max={9}
              step={0.5}
              value={minRating}
              onChange={(e) => onRatingChange(Number(e.target.value))}
              className="w-full accent-accent-500 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-foreground-400 mt-1">
              <span>0 分</span>
              <span>9 分</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 border-t border-background-200">
          <div className="flex items-center gap-2">
            <i className="ri-sort-desc text-foreground-500"></i>
            {sortOptions.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => onSortChange(o.key)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === o.key
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}